/**
 * InputRow — the ubiquitous "speak…" strip: ASK YUO capsule (left) +
 * pill input with a coloured dot + stone-send button (right). No arrows,
 * no glyphs, just light.
 */
import { INK, FONT, ROOMS, STONE_GRADIENTS } from "@/lib/yuoDesign";
import { useResponsive } from "@/lib/responsive";
import { StoneButton } from "@/components/mobile/MobileHeader";
import AutoTextarea from "@/components/yuo/AutoTextarea";

export default function InputRow({
  room = "mirror",
  value, onChange, onSend, onAskYuo,
  placeholder = "speak…",
  askYuoTestId = "ask-yuo",
  sendTestId = "input-send",
  inputTestId = "room-input",
  disabled = false,
  extraLeft,
}) {
  const meta = ROOMS[room] || ROOMS.mirror;
  const { isPhone } = useResponsive();

  if (isPhone) {
    return (
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, padding: "6px 14px 8px", flex: "0 0 auto" }}>
        {extraLeft}
        {onAskYuo && <StoneButton room="symbiosis" size={40} onClick={onAskYuo} disabled={disabled} testId={askYuoTestId} ariaLabel="Ask YUO" />}
        <AutoTextarea data-testid={inputTestId} value={value} onChange={onChange} onSend={onSend} placeholder={placeholder} disabled={disabled} maxHeight={160}
          style={{
            flex: "1 1 0", minWidth: 0, minHeight: 40, borderRadius: 20, border: "none", outline: "none", padding: "10px 16px",
            background: "var(--m-field-bg)", color: "var(--yuo-ink)", fontFamily: FONT, fontSize: 13,
            boxShadow: `inset 0 0 0 1px rgba(${meta.rgb},0.22)`,
          }} />
        <StoneButton room={room} size={40} onClick={onSend} disabled={disabled || !String(value || "").trim()} testId={sendTestId} ariaLabel="Send" className="yuo-send-stone" />
      </div>
    );
  }

  return (
    <div style={{
      position: "relative", display: "flex", justifyContent: "center",
      padding: "6px 24px 14px",
    }}>
      <div style={{ width: "100%", maxWidth: 1060, display: "flex", alignItems: "flex-end", gap: 14 }}>
        {onAskYuo && (
          <button
            data-testid={askYuoTestId}
            onClick={onAskYuo}
            style={{
              flex: "0 0 auto", display: "flex", alignItems: "center", gap: 10,
              fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase",
              padding: "14px 24px", borderRadius: 999, border: "none",
              background: "var(--role-green-askyuo-bg)",
              color: "var(--role-green-ink)",
              boxShadow: "var(--role-green-askyuo-glow)",
              cursor: "pointer", fontFamily: FONT,
            }}>
            <span style={{
              width: 15, height: 15, borderRadius: "50%",
              background: STONE_GRADIENTS.symbiosis,
              boxShadow: "0 0 14px rgba(var(--role-green-rgb),0.8)",
            }} />
            ASK YUO
          </button>
        )}

        {extraLeft}

        <div style={{
          flex: "1 1 0", display: "flex", alignItems: "flex-end", gap: 16,
          padding: "6px 8px 6px 20px", borderRadius: 31,
          background: "var(--yuo-input-panel-bg)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: `0 0 44px rgba(${meta.rgb},0.20) inset, var(--yuo-input-panel-shadow-dark)`,
        }}>
          <span style={{
            flex: "0 0 auto", marginBottom: 20,
            width: 9, height: 9, borderRadius: "50%",
            background: meta.hex,
            boxShadow: `0 0 14px ${meta.hex}`,
          }} />
          <AutoTextarea
            data-testid={inputTestId}
            value={value || ""}
            onChange={onChange}
            onSend={onSend}
            placeholder={placeholder}
            disabled={disabled}
            maxHeight={200}
            style={{
              flex: "1 1 0", minWidth: 0, background: "var(--yuo-input-field-bg)",
              border: "none", outline: "none", color: INK,
              fontFamily: FONT, fontSize: 13.5,
              padding: "15px 16px",
            }}
          />
          <button
            data-testid={sendTestId}
            onClick={onSend}
            disabled={disabled || !(value || "").trim()}
            aria-label="Send"
            className="yuo-send-stone"
            style={{
              flex: "0 0 auto", width: 50, height: 50, borderRadius: "50%",
              border: "none", padding: 0,
              background: STONE_GRADIENTS[room] || STONE_GRADIENTS.mirror,
              cursor: (value || "").trim() ? "pointer" : "default",
              opacity: (value || "").trim() ? 1 : 0.6,
              boxShadow: `0 0 30px rgba(${meta.rgb},0.55), inset -6px -8px 16px rgba(0,0,0,0.8)`,
            }}
          />
        </div>
      </div>
    </div>
  );
}
